import { useState, useEffect } from 'react';
import moment from 'moment';

// Services
import { getPairingStats, getSightingStats } from '../../../services/stats.services';
import AuthService from '../../../services/auth.service';

import { dashboardContent } from '../../../constants/Constants';

import PairingsChart from './components/PairingsChart';
import FilterControls from './components/FilterControls';

import SightingsChart from './components/SightingsChart';
import { DevicesStats } from './components/DevicesStats';

import GlobalFleetDashboard from './GlobalFleetDashboard/GlobalFleetDashboard';

import './dashboard.scss';

type DashboardTab = 'dashboard' | 'stats';

// Stats are internal-only, so external users get the Global Fleet tab alone.
const dashboardTabs: { key: DashboardTab; label: string; internalOnly?: boolean }[] = [
  { key: 'dashboard', label: dashboardContent.tabs.dashboard },
  { key: 'stats', label: dashboardContent.tabs.stats, internalOnly: true },
];

export default function Dashboard() {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 29);

  const [pairingsStats, setPairingsStats] = useState({
    total: 0,
    averageDaily: 0,
    daily: [],
  });

  const [sightingsStats, setSightingsStats] = useState({
    total: 0,
    averageDaily: 0,
    daily: [],
  });

  const [from, setFrom] = useState(thirtyDaysAgo);
  const [to, setTo] = useState(today);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DashboardTab>('dashboard');

  const isExternalUser = AuthService.getStoredUserProfile()?.isInternal === false;

  const fetchPairings = async (fromDate?, toDate?) => {
    try {
      setErrorMessage(null);

      const res = await getPairingStats(
        fromDate ? moment(fromDate).startOf('day').toISOString() : undefined,
        toDate ? moment(toDate).endOf('day').toISOString() : undefined,
      );

      const stats = res?.data ?? {
        total: 0,
        averageDaily: 0,
        daily: [],
      };

      setPairingsStats({
        total: stats.total,
        averageDaily: stats.averageDaily,
        daily: (stats.daily ?? []).map((d) => ({
          date: d.date?.split('T')[0],
          count: d.count ?? 0,
        })),
      });
    } catch (err: any) {
      console.error('Pairing fetch error:', err);

      setErrorMessage(dashboardContent.errors.fetchPairing);
    }
  };

  const fetchSightings = async (fromDate?, toDate?) => {
    try {
      setErrorMessage(null);

      const res = await getSightingStats(
        fromDate ? moment(fromDate).startOf('day').toISOString() : undefined,
        toDate ? moment(toDate).endOf('day').toISOString() : undefined,
      );

      const stats = res?.data ?? {
        total: 0,
        averageDaily: 0,
        daily: [],
      };

      setSightingsStats({
        total: stats.total,
        averageDaily: stats.averageDaily,
        daily: (stats.daily ?? []).map((d) => ({
          date: d.date?.split('T')[0],
          count: d.count ?? 0,
        })),
      });
    } catch (err: any) {
      console.error('Sightings fetch error:', err);

      setErrorMessage(dashboardContent.errors.fetchSightings);
    }
  };

  useEffect(() => {
    // Pairing/Sightings stats are internal-only, so skip the fetch entirely for external users.
    if (isExternalUser) return;

    fetchPairings(thirtyDaysAgo, today);
    fetchSightings(thirtyDaysAgo, today);
  }, [isExternalUser]);

  const visibleTabs = dashboardTabs.filter((tab) => !(tab.internalOnly && isExternalUser));

  return (
    <>
      <div className='dashboard-tabs'>
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            type='button'
            className={`dashboard-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Global Fleet Map: temporarily disabled, placeholder shown for now */}
      {activeTab === 'dashboard' && <GlobalFleetDashboard />}
      {/* {activeTab === 'dashboard' && <ComingSoon title={dashboardContent.comingSoon.dashboard.title} />} */}

      {activeTab === 'stats' && !isExternalUser && (
        <>
          <div className='dashboard-screen'>
            <div className='mx-auto max-w-7xl p-6 space-y-6'>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '2px',
                  borderBottom: '10px',
                }}
              >
                <section
                  style={{
                    flex: '1 1 calc(50% - 12px)',
                    minWidth: '320px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '10px',
                    boxSizing: 'border-box',
                    backgroundColor: '#fcfcfc',
                  }}
                >
                  <div
                    style={{
                      padding: '8px 16px',
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        marginBottom: '8px',
                      }}
                    >
                      {dashboardContent.titles.pairingStatistics}
                    </h4>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                      }}
                    >
                      <FilterControls
                        from={from}
                        to={to}
                        setFrom={setFrom}
                        setTo={setTo}
                        onSearch={() => fetchPairings(from, to)}
                        onRefresh={() => {
                          setFrom(thirtyDaysAgo);
                          setTo(today);
                          fetchPairings(thirtyDaysAgo, today);
                        }}
                      />
                    </div>
                  </div>

                  {errorMessage && (
                    <div
                      style={{
                        backgroundColor: '#fee2e2',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '1px solid #fca5a5',
                        color: '#b91c1c',
                        marginBottom: '10px',
                      }}
                    >
                      {errorMessage}
                    </div>
                  )}

                  <PairingsChart data={pairingsStats.daily} total={pairingsStats.total} dailyAverage={pairingsStats.averageDaily} />
                </section>

                <section
                  style={{
                    flex: '1 1 calc(50% - 12px)',
                    minWidth: '320px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '10px',
                    boxSizing: 'border-box',
                    backgroundColor: '#fcfcfc',
                  }}
                >
                  <div
                    style={{
                      padding: '8px 16px',
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        marginBottom: '8px',
                      }}
                    >
                      {dashboardContent.titles.sightingsStatistics}
                    </h4>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                      }}
                    >
                      <FilterControls
                        from={from}
                        to={to}
                        setFrom={setFrom}
                        setTo={setTo}
                        onSearch={() => fetchSightings(from, to)}
                        onRefresh={() => {
                          setFrom(thirtyDaysAgo);
                          setTo(today);
                          fetchSightings(thirtyDaysAgo, today);
                        }}
                      />
                    </div>
                  </div>

                  {errorMessage && (
                    <div
                      style={{
                        backgroundColor: '#fee2e2',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '1px solid #fca5a5',
                        color: '#b91c1c',
                        marginBottom: '10px',
                      }}
                    >
                      {errorMessage}
                    </div>
                  )}

                  <SightingsChart data={sightingsStats.daily} total={sightingsStats.total} dailyAverage={sightingsStats.averageDaily} />
                </section>
              </div>
            </div>
          </div>

          <DevicesStats />
        </>
      )}
    </>
  );
}
